import type { Response } from 'express';

interface SSEClient {
  id: string;
  res: Response;
  connectedAt: number;
}

/**
 * ClientManager - manages SSE client connections
 */
export class ClientManager {
  private clients: Map<string, SSEClient> = new Map();
  private debug: boolean = false;

  constructor(options: { debug?: boolean } = {}) {
    this.debug = options.debug ?? false;
  }

  private log(...args: unknown[]): void {
    if (this.debug) {
      console.log('[ClientManager]', ...args);
    }
  }

  /**
   * Generate a unique client ID
   */
  generateClientId(): string {
    return `client_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
  }

  /**
   * Add a new client connection
   */
  addClient(id: string, res: Response): void {
    this.clients.set(id, {
      id,
      res,
      connectedAt: Date.now(),
    });
    this.log(`Client connected: ${id}, total clients: ${this.clients.size}`);
  }

  /**
   * Remove a client connection
   */
  removeClient(id: string): void {
    if (this.clients.has(id)) {
      this.clients.delete(id);
      this.log(`Client disconnected: ${id}, total clients: ${this.clients.size}`);
    }
  }

  /**
   * Get current client count
   */
  getClientCount(): number {
    return this.clients.size;
  }

  /**
   * Check if a client exists
   */
  hasClient(id: string): boolean {
    return this.clients.has(id);
  }

  /**
   * Send SSE event to a specific client
   */
  sendToClient(clientId: string, event: string, data: unknown): boolean {
    const client = this.clients.get(clientId);
    if (!client) {
      return false;
    }

    try {
      const message = this.formatSSEMessage(event, data);
      client.res.write(message);
      return true;
    } catch (error) {
      this.log(`Failed to send to client ${clientId}:`, error);
      // Remove client if send fails
      this.removeClient(clientId);
      return false;
    }
  }

  /**
   * Broadcast SSE event to all clients
   */
  broadcast(event: string, data: unknown): void {
    const message = this.formatSSEMessage(event, data);
    const failedClients: string[] = [];

    Array.from(this.clients.entries()).forEach(([id, client]) => {
      try {
        client.res.write(message);
      } catch (error) {
        this.log(`Broadcast failed for client ${id}:`, error);
        failedClients.push(id);
      }
    });

    // Remove failed clients
    failedClients.forEach(id => {
      this.removeClient(id);
    });
  }

  /**
   * Format SSE message
   * SSE format: event: <event>\ndata: <json>\n\n
   */
  private formatSSEMessage(event: string, data: unknown): string {
    const jsonData = JSON.stringify(data);
    return `event: ${event}\ndata: ${jsonData}\n\n`;
  }

  /**
   * Get all client IDs
   */
  getClientIds(): string[] {
    return Array.from(this.clients.keys());
  }

  /**
   * Get client info
   */
  getClientInfo(id: string): { id: string; connectedAt: number } | null {
    const client = this.clients.get(id);
    if (!client) {
      return null;
    }
    return {
      id: client.id,
      connectedAt: client.connectedAt,
    };
  }

  /**
   * Close all client connections
   */
  closeAll(): void {
    this.log(`Closing all ${this.clients.size} clients`);
    Array.from(this.clients.entries()).forEach(([id, client]) => {
      try {
        client.res.end();
      } catch (error) {
        this.log(`Error closing client ${id}:`, error);
      }
    });
    this.clients.clear();
  }
}
